import React, { useState } from "react";
import { PASS_PROMO_DATE_FORMATS, PASS_PROMO_STATES } from "../../constants/pass";
import { formatDateInEnglish } from "../../helpers/dateTimeHelper";
import usePassPromo from "../../hook/usePassPromo";
import arrowIcon from "../../icons/pass-banner-arrow.svg";
import closeIcon from "../../icons/pass-banner-close.svg";
import PassPromoBadge from "./PassPromoBadge";
import PassPromoCountdown from "./PassPromoCountdown";
import { usePassText } from "./passI18n";
import {
  formatPromoDayLabel,
  formatPromoMonthLabel,
  splitPromoHighlight,
} from "./passPromoFormat";
import "./PassPromo.css";

const STRIP_CONTENT = {
  [PASS_PROMO_STATES.BEFORE_ACTIVATED]: {
    titleKey: "DOTORE_PASS.PROMO.BEFORE.TITLE",
    subtitleKey: "DOTORE_PASS.PROMO.BEFORE.SUBTITLE",
    ctaKey: "DOTORE_PASS.PROMO.BEFORE.CTA",
    dateFormat: PASS_PROMO_DATE_FORMATS.LONG_MONTH_DAY,
  },
  [PASS_PROMO_STATES.ACTIVE]: {
    titleKey: "DOTORE_PASS.PROMO.ACTIVE.TITLE",
    subtitleKey: "DOTORE_PASS.PROMO.ACTIVE.SUBTITLE",
    ctaKey: "DOTORE_PASS.PROMO.ACTIVE.CTA",
    dateFormat: PASS_PROMO_DATE_FORMATS.SHORT_MONTH_DAY,
  },
};

export default function PassPromoStrip({ ctaHref, onCtaClick, enabled, productCode, offerEndDate }) {
  const translate = usePassText();
  const { state, highlightedDate } = usePassPromo({ enabled, productCode, offerEndDate });
  const [dismissed, setDismissed] = useState(false);

  if (!state || !highlightedDate || dismissed) return null;

  const content = STRIP_CONTENT[state];
  const showCountdown = state === PASS_PROMO_STATES.ACTIVE;
  const formattedDate = formatDateInEnglish(highlightedDate, content.dateFormat);
  const titleParts = splitPromoHighlight(translate(content.titleKey, { date: formattedDate }));

  const handleCtaClick = (event) => {
    if (!onCtaClick) return;
    event.preventDefault();
    onCtaClick(state);
  };

  const badge = (
    <PassPromoBadge
      month={formatPromoMonthLabel(highlightedDate)}
      day={formatPromoDayLabel(highlightedDate)}
      freeLabel={translate("DOTORE_PASS.PROMO.FREE")}
    />
  );

  return (
    <div className={`dp-promo dp-promo-strip${showCountdown ? "" : " dp-promo-strip--offer"}`}>
      <div className="dp-promo-strip-inner">
        <div className="dp-promo-strip-lead">
          {badge}
          <div className="dp-promo-strip-copy">
            <p className="dp-promo-strip-title">
              {titleParts.map((part, index) =>
                part.highlighted ? (
                  <span className="dp-promo-strip-title-mark" key={index}>
                    {part.value}
                  </span>
                ) : (
                  <React.Fragment key={index}>{part.value}</React.Fragment>
                ),
              )}
            </p>
            <p className="dp-promo-strip-sub">
              {translate(content.subtitleKey, { date: formattedDate })}
            </p>
          </div>
        </div>

        {showCountdown && <PassPromoCountdown endDate={highlightedDate} />}

        {!showCountdown && content.ctaKey && (
          <a className="dp-promo-strip-cta" href={ctaHref || "#"} onClick={handleCtaClick}>
            {translate(content.ctaKey)}
            <img className="dp-promo-strip-cta-icon" src={arrowIcon} alt="" aria-hidden="true" />
          </a>
        )}
      </div>

      <button
        className="dp-promo-strip-close"
        type="button"
        aria-label={translate("DOTORE_PASS.PROMO.CLOSE")}
        onClick={() => setDismissed(true)}
      >
        <img src={closeIcon} alt="" aria-hidden="true" />
      </button>
    </div>
  );
}
